import type { TypedDocumentNode } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';
import { useCallback } from 'react';

import type {
  BenchFilterInput,
  Rarity,
  RangeInput,
  SeasonEligibility,
  Sport,
  SupportedCurrency,
} from '#src/__generated__/globalTypes.ts';
import { namedOperations } from '#src/__generated__/operations.ts';
import type {
  FootballComposeFilters,
  NBAComposeFilters,
} from '#src/components/compose/types.ts';
import type {
  CreditCardPaymentMethod,
  PaymentMethod as DepositMethod,
} from '#src/components/wallet/BankEthAccounting/AddFundsToFiatWallet/types.tsx';
import type { AlgoliaCardIndexesName } from '#src/contexts/config/index.ts';
import { useConfigContext } from '#src/contexts/config/index.ts';
import type {
  PaymentMethod,
  PaymentProvider_paymentMethod,
} from '#src/lib/paymentMethod.ts';
import type {
  PlayerSearchView,
  StatsRarity,
} from '#src/lib/playerSearchView.ts';

import type {
  UpdateLifecycleMutation,
  UpdateLifecycleMutationVariables,
} from './__generated__/useLifecycle.graphql.ts';
import { useIsMobileApp } from './useIsMobileApp.ts';

type FootballTasksUpdate = 'new' | 'created' | 'completed' | 'seen';

export enum LIFECYCLE {
  sawCaptainTuto = 'sawCaptainTuto',
  sawCollectionCardTuto = 'sawCollectionCardTuto',
  sawMarketplaceOnboarding = 'sawMarketplaceOnboarding',
  sawManagersSalesOnboarding = 'sawManagersSalesOnboarding',
  sawStarterPacksOnboarding = 'sawStarterPacksOnboarding',
  transferMarket = 'transferMarket',
  newSignings = 'newSignings',
  nextTimeUserNeedsCheckSecurity = 'nextTimeUserNeedsCheckSecurity',
  nextTimeUserShouldSeeActivateFiatWalletOnListing = 'nextTimeUserShouldSeeActivateFiatWalletOnListing',
  nextTimeUserShouldSeeAcceptedCurrenciesOnListing = 'nextTimeUserShouldSeeAcceptedCurrenciesOnListing',
  userHasSetupWallet = 'userHasSetupWallet',
  nextTimeUserShouldSetupWallet = 'nextTimeUserShouldSetupWallet',
  sawUsSportsCollectionsDialog = 'sawUsSportsCollectionsDialog',
  lastSaleDuration = 'lastSaleDuration',
  sawMlbDraftErrorPrompt = 'sawMlbDraftErrorPrompt',
  sawMlbComposeTeamOnboarding = 'sawMlbComposeTeamOnboarding',
  inSeasonFilterEnabled = 'inSeasonFilterEnabled',
  latestSearchedItems = 'latestSearchedItems',
  sawMlbOnboarding = 'sawMlbOnboarding',
  sawMlbDraftWelcome = 'sawMlbDraftWelcome',
  sawNbaOnboarding = 'sawNbaOnboarding',
  marketSorts = 'marketSorts',
  sawMlbDraftEducationDialog = 'sawMlbDraftEducationDialog',
  saw100PersonLeaderboardDialog = 'saw100PersonLeaderboardDialog',
  hideActivity = 'hideActivity',
  disable3D = 'disable3D',
  hideOnlineStatus = 'hideOnlineStatus',
  preferredPaymentMethod = 'preferredPaymentMethod',
  preferredDepositMethod = 'preferredDepositMethod',
  sawMinorsUnlockedDialog = 'sawMinorsUnlockedDialog',
  sawCommonLevel1UnlockedDialog = 'sawCommonLevel1UnlockedDialog',
  sawCommonLevel2UnlockedDialog = 'sawCommonLevel2UnlockedDialog',
  sawCommonLevel3UnlockedDialog = 'sawCommonLevel3UnlockedDialog',
  sawCommonLevel4UnlockedDialog = 'sawCommonLevel4UnlockedDialog',
  sawCommonLevel5UnlockedDialog = 'sawCommonLevel5UnlockedDialog',
  sawRewardUnlockedDialog = 'sawRewardsUnlockedDialog',
  referenceCurrencyForSale = 'referenceCurrencyForSale',
  sawMlbClubShopIntro = 'sawMlbClubShopIntro',
  hideReferralBanner = 'hideReferralBanner',
  sawKickoffWelcomeToKickoff = 'sawKickoffWelcomeToKickoff',
  showTrainingLineups = 'showTrainingLineups',
  showOnlyCompetitionsEntered = 'showOnlyCompetitionsEntered',
  sawChallengesOnboarding = 'sawChallengesOnboarding',
  sawTacticsOnboarding = 'sawTacticsOnboarding',
  doNotShowRivalsDownloadApp = 'doNotShowRivalsDownloadApp',
  doNotShowAddAuctionReminderDownloadApp = 'doNotShowAddAuctionReminderDownloadApp',
  sawForYouFirstTime = 'sawForYouFirstTime',
  unselectConversionCreditByDefaultFromSettings = 'unselectConversionCreditByDefaultFromSettings',
  sawWelcomeToSeasonsArena = 'sawWelcomeToSeasonsArena',
  sawSocialWallDialog = 'sawSocialWallDialog',
  hideConversionCreditBanner = 'hideConversionCreditBanner',
  sawEuropeanChampionshipTooltip = 'sawEuropeanChampionshipTooltip',
  sawRivalsLeagueOnboarding = 'sawRivalsLeagueOnboarding',
  playRivalsLeagueReminderLastDate = 'playRivalsLeagueReminderLastDate',
  sawRivalsLeagueMatchSlugs = 'sawRivalsLeagueMatchSlugs',
  sawRivalsPlayButtonOnboarding = 'sawRivalsPlayButtonOnboarding',
  sawBoxOnboarding = 'sawBoxOnboarding',
  sawRivalsLeagueCrossMode = 'sawRivalsLeagueCrossMode',
  sawRivalsPlayPageHighlight = 'sawRivalsPlayPageHighlight',
  sawRivalsLeagueEndingStory = 'sawRivalsLeagueEndingStory',
  sawEarlyAccessAnnouncementDialog = 'sawEarlyAccessAnnouncementDialog',
  sawRevealAnnouncementDialog = 'sawRevealAnnouncementDialog',
  sawStartedEarlyAccessAnnouncementDialog = 'sawStartedEarlyAccessAnnouncementDialog',
  sawChallengerEarlyAccessAnnouncementDialog = 'sawChallengerEarlyAccessAnnouncementDialog',
  sawChampionEarlyAccessAnnouncementDialog = 'sawChampionEarlyAccessAnnouncementDialog',
  sawSubstitutesFTUE = 'sawSubstitutesFTUE',
  sawChaseHelpDialog = 'sawChaseHelpDialog',
  sawComposeTeamOnboarding = 'sawComposeTeamOnboarding',
  sawComposeTeamHighlights = 'sawComposeTeamHighlights',
  sawComposeTeamBenchFooterOnboarding = 'sawComposeTeamBenchFooterOnboarding',
  sawMySquadOnboarding = 'sawMySquadOnboarding',
  sawWarmUpAnnouncement = 'sawWarmUpAnnouncement',
  sawNBAS3LobbyOnboarding = 'sawNBAS3LobbyOnboarding',
  sawPushNotificationsPromptInWebview = 'sawNotificationsPromptInWebview',
  sawGalleryOnboarding = 'sawGalleryOnboarding',
  sawCommonMarketOnboarding = 'sawCommonMarketOnboarding',
  sawSeriesHotStreakOnboarding = 'sawSeriesHotStreakOnboarding',
  sawSeriesBoardOnboarding = 'sawSeriesBoardOnboarding',
  sawSeriesScheduleScreen = 'sawSeriesScheduleScreen',
  unlockedDialogSeenSo5LeagueNames = 'unlockedDialogSeenSo5LeagueNames',
  sawMVPOnboarding = 'sawMVPOnboarding',
  sawMyCardsOnboarding = 'sawMyCardsOnboarding',
  sawPlayProFixturePageOnboarding = 'sawPlayProFixturePageOnboarding',
  sawDailyHoopsOnboarding = 'sawDailyHoopsOnboarding',
  sawNBAProUnlockScreen = 'sawNBAProUnlockScreen',
  sawNBADailyHoopsUnlockScreen = 'sawNBADailyHoopsUnlockScreen',
  sawDailyClaimButton = 'sawDailyClaimButton',
  sawNeonChristmasDialog = 'sawNeonChristmasDialog',
  sawNeonChristmasStartedDialog = 'sawNeonChristmasStartedDialog',
  DEPRECATEDneonChristmasCalendar = 'neonChristmasCalendar',
  neonChristmasCalendarProgress = 'neonChristmasCalendarProgress',
  lastVisitedMyCardsRarity = 'lastVisitedMyCardsRarity',
  lastLobbySort = 'lastLobbySort',
  sawComposeTeamTutorial = 'sawComposeTeamTutorial',
  statsManager = 'statsManager',
  sawFirstTimeStatsManager = 'sawFirstTimeStatsManager',
  hasDismissedExpiredPromoDepositBanner = 'hasDismissedExpiredPromoDepositBanner',
  composeLastViewUsed = 'composeLastViewUsed',
  composeFilters = 'composeFilters',
  seriesComposeFilters = 'seriesComposeFilters',
  sawTiersOnboarding = 'sawTiersOnboarding',
  sawNBATutorialVideos = 'sawNBATutorialVideos',
  sawNBAIntroductionTutorialVideo = 'sawNBAIntroductionTutorialVideo',
  sawNBATypesOfCardsTutorialVideo = 'sawNBATypesOfCardsTutorialVideo',
  sawNBAMarketplaceTutorialVideo = 'sawNBAMarketplaceTutorialVideo',
  sawNBACompetitionsTutorialVideo = 'sawNBACompetitionsTutorialVideo',
  sawCommonPacksOnboarding = 'sawCommonPacksOnboarding',
  sawLineupBonusOnboarding = 'sawLineupBonusOnboarding',
  sawStreaksTuto = 'sawStreaksTuto',
  sawDropOnboarding = 'sawDropOnboarding',
  sawMarketVideo = 'sawMarketVideo',
  sawWhatIsSorareVideo = 'sawWhatIsSorareVideo',
  sawGetStartedVideo = 'sawGetStartedVideo',
  sawScoringSystemVideo = 'sawScoringSystemVideo',
  sawCompetitionsVideo = 'sawCompetitionsVideo',
  sawMLBComposeTeamHighlights = 'sawMLBComposeTeamHighlights',
  sawMLBLobbyOnboarding = 'sawMLBLobbyOnboarding',
  sawDailySwingOnboarding = 'sawDailySwingOnboarding',
  sawMLBDailyClaimButton = 'sawMLBDailyClaimButton',
  sawDecisivePlayerPickerOnboarding = 'sawDecisivePlayerPickerOnboarding',
  sawVaultDecisivePlayerPickerOnboarding = 'sawVaultDecisivePlayerPickerOnboarding',
  footballDailyTasksUpdate = 'footballDailyTasksUpdate',
  footballGameWeeklyTasksUpdate = 'footballGameWeeklyTasksUpdate',
  footballHotStreakTasksUpdate = 'footballHotStreakTasksUpdate',
  sawDropChallengePickerOnboarding = 'sawDropChallengePickerOnboarding',
  sawMlbSealingOnboarding = 'sawMlbSealingOnboarding',
  lastDismissedSorareWatchlists = 'lastDismissedSorareWatchlists',
  sawComposeTeamAdvancedViewOnboarding = 'sawComposeTeamAdvancedViewOnboarding',
  sawLineViewOnboarding = 'sawLineViewOnboarding',
  pinnedShardRarity = 'pinnedShardRarity',
  pinnedXpRarity = 'pinnedXpRarity',
  sawLoyaltyBoxDialog = 'sawLoyaltyBoxDialog',
  playerPageFilters = 'playerPageFilters',
  missionsRarityFilter = 'missionsRarityFilter',
  newFootballSeasonRarityFilter = 'footballNewSeasonRarityFilter',
  sawDepositButton = 'sawDepositButton',
  sawWelcomeToSeason26Dialog = 'sawWelcomeToSeason26Dialog',
  sawNbaWelcomeToSeason26Dialog = 'sawNbaWelcomeToSeason26Dialog',
  showS26WelcomeBack = 'showS26WelcomeBack',
  experimentalLightMode = 'lightMode',
  matchCenterFilters = 'matchCenterFilters',
  marketHomeFilters = 'marketHomeFilters',
  sawQuickPlayTutorial = 'sawQuickPlayTutorial',
  lastVisitedPlayTab = 'lastVisitedPlayTab',
  lastVisitedBoardView = 'lastVisitedBoardView',
  sawCommonMissionsTutorial = 'sawCommonMissionsTutorial',
  sawSolanaFtue = 'sawSolanaFtue',
  sawMlbOffseasonOnboarding = 'sawMlbOffseasonOnboarding',
  storedLastNavigation = 'storedLastNavigation',
  welcomePackAutoOpen = 'welcomePackAutoOpen',
  sawNewThresholdsDialog = 'sawNewThresholdsDialog',
  sawInviteFriendsFTUE = 'sawInviteFriendsFTUE',
  sawJonumVerifiedDialog = 'sawJonumVerifiedDialog',
  dismissedMissionHighlightBannerSlugs = 'dismissedMissionHighlightBannerSlugs',
}

type SeriesComposeFilters = {
  averageScoreRange: Nullable<RangeInput>;
  showAffordableOnly: boolean;
  includeUnavailablePlayers: boolean;
  includeUsed: boolean;
  startersOnly: boolean;
  starterOddsBasisPointsRange: { min: number; max: number };
  enablePlayingInFilter: boolean;
  playingInHours: number;
  sortType: BenchFilterInput['sortType'];
};

export type LifecycleEntries = {
  [LIFECYCLE.sawCaptainTuto]?: boolean;
  [LIFECYCLE.sawCollectionCardTuto]?: boolean;
  [LIFECYCLE.sawMarketplaceOnboarding]?: boolean;
  [LIFECYCLE.sawManagersSalesOnboarding]?: boolean;
  [LIFECYCLE.sawStarterPacksOnboarding]?: boolean;
  [LIFECYCLE.sawChaseHelpDialog]?: boolean;
  [LIFECYCLE.transferMarket]?: string;
  [LIFECYCLE.newSignings]?: string;
  [LIFECYCLE.nextTimeUserNeedsCheckSecurity]?: string;
  [LIFECYCLE.nextTimeUserShouldSeeActivateFiatWalletOnListing]?: string;
  [LIFECYCLE.nextTimeUserShouldSeeAcceptedCurrenciesOnListing]?: string;
  [LIFECYCLE.userHasSetupWallet]?: boolean;
  [LIFECYCLE.nextTimeUserShouldSetupWallet]?: string;
  [LIFECYCLE.sawUsSportsCollectionsDialog]?: boolean;
  [LIFECYCLE.lastSaleDuration]?: number;
  [LIFECYCLE.sawMlbDraftErrorPrompt]?: boolean;
  [LIFECYCLE.sawMlbComposeTeamOnboarding]?: boolean;
  [LIFECYCLE.saw100PersonLeaderboardDialog]?: boolean;
  [LIFECYCLE.inSeasonFilterEnabled]?: boolean;
  [LIFECYCLE.latestSearchedItems]?: {
    [sport: string]: {
      objectID: string;
      index: string;
      sport: Sport;
    }[];
  };
  [LIFECYCLE.sawMlbOnboarding]?: boolean;
  [LIFECYCLE.sawMlbDraftWelcome]?: boolean;
  [LIFECYCLE.sawNbaOnboarding]?: boolean;
  [LIFECYCLE.marketSorts]?: Record<string, AlgoliaCardIndexesName>;
  [LIFECYCLE.sawMlbDraftEducationDialog]?: boolean;
  [LIFECYCLE.hideActivity]?: boolean;
  [LIFECYCLE.disable3D]?: boolean;
  [LIFECYCLE.hideOnlineStatus]?: boolean;
  [LIFECYCLE.referenceCurrencyForSale]: SupportedCurrency;
  [LIFECYCLE.sawMinorsUnlockedDialog]?: boolean;
  [LIFECYCLE.sawCommonLevel1UnlockedDialog]?: boolean;
  [LIFECYCLE.sawCommonLevel2UnlockedDialog]?: boolean;
  [LIFECYCLE.sawCommonLevel3UnlockedDialog]?: boolean;
  [LIFECYCLE.sawCommonLevel4UnlockedDialog]?: boolean;
  [LIFECYCLE.sawCommonLevel5UnlockedDialog]?: boolean;
  [LIFECYCLE.sawRewardUnlockedDialog]?: boolean;
  [LIFECYCLE.preferredPaymentMethod]?:
    | PaymentProvider_paymentMethod
    | PaymentMethod.CRYPTO_WALLET
    | PaymentMethod.FIAT_WALLET
    // @deprecated Use CRYPTO_WALLET instead. Kept for backward compatibility
    | PaymentMethod.ETH_WALLET;
  [LIFECYCLE.preferredDepositMethod]?: DepositMethod | CreditCardPaymentMethod;
  [LIFECYCLE.sawMlbClubShopIntro]?: boolean;
  [LIFECYCLE.hideReferralBanner]?: boolean;
  [LIFECYCLE.sawKickoffWelcomeToKickoff]?: boolean;
  [LIFECYCLE.showTrainingLineups]?: boolean;
  [LIFECYCLE.showOnlyCompetitionsEntered]?: boolean;
  [LIFECYCLE.sawChallengesOnboarding]?: boolean;
  [LIFECYCLE.sawTacticsOnboarding]?: boolean;
  [LIFECYCLE.doNotShowRivalsDownloadApp]?: boolean;
  [LIFECYCLE.doNotShowAddAuctionReminderDownloadApp]?: boolean;

  [LIFECYCLE.sawForYouFirstTime]?: boolean;
  [LIFECYCLE.unselectConversionCreditByDefaultFromSettings]?: boolean;
  [LIFECYCLE.sawWelcomeToSeasonsArena]?: boolean;
  [LIFECYCLE.sawSocialWallDialog]?: boolean;
  [LIFECYCLE.hideConversionCreditBanner]?: boolean;
  [LIFECYCLE.sawEuropeanChampionshipTooltip]?: boolean;
  [LIFECYCLE.sawRivalsLeagueOnboarding]?: boolean;
  [LIFECYCLE.playRivalsLeagueReminderLastDate]?: string;
  [LIFECYCLE.sawRivalsLeagueMatchSlugs]?: [string];
  [LIFECYCLE.sawRivalsPlayButtonOnboarding]?: boolean;
  [LIFECYCLE.sawBoxOnboarding]?: {
    limitedCard?: boolean;
    essence?: boolean;
  };
  [LIFECYCLE.sawRivalsLeagueCrossMode]?: boolean;
  [LIFECYCLE.sawRivalsPlayPageHighlight]?: boolean;
  [LIFECYCLE.sawRivalsLeagueEndingStory]?: boolean;
  [LIFECYCLE.sawRevealAnnouncementDialog]?: boolean;
  [LIFECYCLE.sawEarlyAccessAnnouncementDialog]?: boolean;
  [LIFECYCLE.sawStartedEarlyAccessAnnouncementDialog]?: boolean;
  [LIFECYCLE.sawSubstitutesFTUE]?: boolean;
  [LIFECYCLE.sawChallengerEarlyAccessAnnouncementDialog]?: boolean;
  [LIFECYCLE.sawChampionEarlyAccessAnnouncementDialog]?: boolean;
  [LIFECYCLE.sawComposeTeamOnboarding]?: boolean;
  [LIFECYCLE.sawComposeTeamHighlights]?: boolean;
  [LIFECYCLE.sawComposeTeamBenchFooterOnboarding]?: boolean;
  [LIFECYCLE.sawMySquadOnboarding]?: boolean;
  [LIFECYCLE.sawWarmUpAnnouncement]?: boolean;
  [LIFECYCLE.sawPushNotificationsPromptInWebview]?: boolean;
  [LIFECYCLE.sawNBAS3LobbyOnboarding]?: boolean;
  [LIFECYCLE.sawGalleryOnboarding]?: boolean;
  [LIFECYCLE.sawCommonMarketOnboarding]?: boolean;
  [LIFECYCLE.unlockedDialogSeenSo5LeagueNames]?: string[];
  [LIFECYCLE.sawMVPOnboarding]?: boolean;
  [LIFECYCLE.sawMyCardsOnboarding]?: boolean;
  [LIFECYCLE.sawPlayProFixturePageOnboarding]?: boolean;
  [LIFECYCLE.sawDailyHoopsOnboarding]?: boolean;
  [LIFECYCLE.sawNBAProUnlockScreen]?: boolean;
  [LIFECYCLE.sawNBADailyHoopsUnlockScreen]?: boolean;
  [LIFECYCLE.sawDailyClaimButton]?: boolean;
  [LIFECYCLE.sawNeonChristmasDialog]?: boolean;
  [LIFECYCLE.sawNeonChristmasStartedDialog]?: boolean;
  [LIFECYCLE.DEPRECATEDneonChristmasCalendar]?: number[];
  [LIFECYCLE.neonChristmasCalendarProgress]?: number[];
  [LIFECYCLE.lastVisitedMyCardsRarity]?: {
    [sport: string]: string;
  };
  [LIFECYCLE.lastVisitedPlayTab]?: {
    [sport: string]: 'quickPlay' | 'play';
  };
  [LIFECYCLE.lastVisitedBoardView]?: {
    [sport: string]: {
      lastVisitedMode?: 'PRO' | 'ARENA_CASH' | 'ARENA_ESSENCE';
      lastVisitedBoardTitle?: string;
      lastVisitedSo5Competition?: string;
      lastVisitedArenaRarity?: Rarity;
    };
  };
  [LIFECYCLE.sawComposeTeamTutorial]?: boolean;
  [LIFECYCLE.statsManager]?: Record<Sport, PlayerSearchView>;
  [LIFECYCLE.sawFirstTimeStatsManager]?: Record<Sport, boolean>;
  [LIFECYCLE.playerPageFilters]?: Record<
    Sport,
    { rarity: StatsRarity; seasonality: SeasonEligibility }
  >;
  [LIFECYCLE.hasDismissedExpiredPromoDepositBanner]?: boolean;
  [LIFECYCLE.composeLastViewUsed]?: string;
  [LIFECYCLE.composeFilters]?: {
    [Sport.FOOTBALL]?: Pick<
      FootballComposeFilters,
      | 'sortType'
      | 'includeNoGame'
      | 'includeUsed'
      | 'startersOnly'
      | 'starterOddsBasisPointsRange'
    >;
    [Sport.NBA]?: Pick<NBAComposeFilters, 'includeNoGame' | 'includeUsed'>;
    [Sport.BASEBALL]?: null;
  };
  [LIFECYCLE.seriesComposeFilters]?: {
    [key in Sport]?: Partial<SeriesComposeFilters>;
  };
  [LIFECYCLE.sawTiersOnboarding]?: boolean;
  [LIFECYCLE.sawNBATutorialVideos]?: boolean;
  [LIFECYCLE.sawNBAIntroductionTutorialVideo]?: boolean;
  [LIFECYCLE.sawNBATypesOfCardsTutorialVideo]?: boolean;
  [LIFECYCLE.sawNBAMarketplaceTutorialVideo]?: boolean;
  [LIFECYCLE.sawNBACompetitionsTutorialVideo]?: boolean;
  [LIFECYCLE.sawCommonPacksOnboarding]?: boolean;
  [LIFECYCLE.sawLineupBonusOnboarding]?: boolean;
  [LIFECYCLE.sawStreaksTuto]?: boolean;
  [LIFECYCLE.sawDropOnboarding]?: boolean;
  [LIFECYCLE.sawMarketVideo]?: boolean;
  [LIFECYCLE.sawWhatIsSorareVideo]?: boolean;
  [LIFECYCLE.sawGetStartedVideo]?: boolean;
  [LIFECYCLE.sawScoringSystemVideo]?: boolean;
  [LIFECYCLE.sawCompetitionsVideo]?: boolean;
  [LIFECYCLE.sawMLBComposeTeamHighlights]?: boolean;
  [LIFECYCLE.sawMLBLobbyOnboarding]?: boolean;
  [LIFECYCLE.sawDailySwingOnboarding]?: boolean;
  [LIFECYCLE.sawMLBDailyClaimButton]?: boolean;
  [LIFECYCLE.sawDecisivePlayerPickerOnboarding]?: boolean;
  [LIFECYCLE.sawVaultDecisivePlayerPickerOnboarding]?: boolean;
  [LIFECYCLE.footballDailyTasksUpdate]?: FootballTasksUpdate;
  [LIFECYCLE.footballGameWeeklyTasksUpdate]?: FootballTasksUpdate;
  [LIFECYCLE.footballHotStreakTasksUpdate]?: FootballTasksUpdate;
  [LIFECYCLE.sawDropChallengePickerOnboarding]?: boolean;
  [LIFECYCLE.sawMlbSealingOnboarding]?: boolean;
  [LIFECYCLE.lastDismissedSorareWatchlists]?: Record<Sport, string>;
  [LIFECYCLE.sawComposeTeamAdvancedViewOnboarding]?: boolean;
  [LIFECYCLE.sawLineViewOnboarding]?: boolean;
  [LIFECYCLE.lastLobbySort]?: 'COMPETITION' | 'RANK';
  [LIFECYCLE.pinnedShardRarity]?: string | false;
  [LIFECYCLE.pinnedXpRarity]?: Rarity | false;
  [LIFECYCLE.sawLoyaltyBoxDialog]?: boolean;
  [LIFECYCLE.missionsRarityFilter]?: {
    [key in Sport]: Rarity;
  };
  [LIFECYCLE.newFootballSeasonRarityFilter]?: Rarity;
  [LIFECYCLE.sawDepositButton]?: boolean;
  [LIFECYCLE.sawWelcomeToSeason26Dialog]?: boolean;
  [LIFECYCLE.sawNbaWelcomeToSeason26Dialog]?: boolean;
  [LIFECYCLE.showS26WelcomeBack]?: boolean;
  [LIFECYCLE.experimentalLightMode]?: boolean;
  [LIFECYCLE.matchCenterFilters]?: {
    sort: 'date' | 'competition';
    withCards: boolean;
  };
  [LIFECYCLE.marketHomeFilters]?: {
    [key in Sport]: {
      rarity: Rarity;
      competitionSlug: string;
    };
  };
  [LIFECYCLE.sawQuickPlayTutorial]?: boolean;
  [LIFECYCLE.sawCommonMissionsTutorial]?: boolean;
  [LIFECYCLE.sawSolanaFtue]?: boolean;
  [LIFECYCLE.sawMlbOffseasonOnboarding]?: boolean;
  [LIFECYCLE.sawSeriesHotStreakOnboarding]?: boolean;
  [LIFECYCLE.sawSeriesBoardOnboarding]?: boolean;
  [LIFECYCLE.sawSeriesScheduleScreen]?: boolean;
  [LIFECYCLE.storedLastNavigation]?: Record<string, unknown>;
  [LIFECYCLE.welcomePackAutoOpen]?: { [slug: string]: Date };
  [LIFECYCLE.sawNewThresholdsDialog]?: boolean;
  [LIFECYCLE.sawInviteFriendsFTUE]?: boolean;
  [LIFECYCLE.sawJonumVerifiedDialog]?: boolean;
  [LIFECYCLE.dismissedMissionHighlightBannerSlugs]?: string[];
};

export type Lifecycle = LifecycleEntries | undefined;
export type LifecycleValue = LifecycleEntries[LIFECYCLE];
export type LifecycleGetter = LifecycleValue;

const UPDATE_LIFECYCLE_MUTATION = gql`
  mutation UpdateLifecycleMutation($input: updateUserSettingsInput!) {
    updateUserSettings(input: $input) {
      userSettings {
        id
        lifecycle
      }
      errors {
        path
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  UpdateLifecycleMutation,
  UpdateLifecycleMutationVariables
>;

export /**
 * We have a parameter here to refetch the configQuery as we do not want to load
 * currentUser in this mutation, as optimisticResponse can cause stale data in
 * Apollo Cache, which breaks field policies (and results in Date objects from
 * GraphQL being returned as strings)
 * MR: https://gitlab.com/sorare/frontend/-/merge_requests/19283
 * Works around: https://github.com/apollographql/apollo-client/issues/9293
 */
const useLifecycle = ({ refetchConfigQuery = false } = {}) => {
  const { currentUser } = useConfigContext();
  const { postMessage, isMobileApp } = useIsMobileApp();
  const [updateLifecycle, { loading }] = useMutation(
    UPDATE_LIFECYCLE_MUTATION,
    {
      refetchQueries: refetchConfigQuery
        ? [namedOperations.Query.ConfigQuery]
        : undefined,
    }
  );

  const update = useCallback(
    async <K extends keyof LifecycleEntries>(
      name: K,
      value: Omit<LifecycleEntries[K], 'undefined'>
    ) => {
      if (!currentUser?.slug) {
        return;
      }
      const lifecycle = currentUser.userSettings.lifecycle as Lifecycle;
      await updateLifecycle({
        variables: {
          input: {
            lifecycle: {
              name,
              value,
            },
          },
        },
        optimisticResponse: {
          updateUserSettings: {
            __typename: 'updateUserSettingsPayload',
            userSettings: {
              __typename: 'UserSettings',
              id: currentUser.userSettings.id,
              lifecycle: {
                ...lifecycle,
                [name]: value,
              },
            },
            errors: [],
          },
        },
      });

      if (isMobileApp) {
        postMessage('updateUserLifecycle', {
          updatedKey: name,
        });
      }
    },
    [updateLifecycle, currentUser, postMessage, isMobileApp]
  );

  const lifecycle = currentUser?.userSettings.lifecycle as Lifecycle;

  return {
    lifecycle,
    update,
    loading,
  };
};
